import { useEffect } from "react";

/**
 * Lightweight per-route SEO — updates <title>, <meta description>,
 * canonical, Open Graph, Twitter, and (optionally) a JSON-LD block.
 * No react-helmet dep — direct DOM writes; Google renders JS.
 *
 * Usage:
 *   useSEO({
 *     title: "Welder Jobs in Noida | Sarthi Associates",
 *     description: "…",
 *     path: "/jobs?category=welder",
 *     jsonLd: { "@context": "https://schema.org", "@type": "JobPosting", … },
 *   });
 */
export default function useSEO({ title, description, path = "", image, jsonLd, lang } = {}) {
  useEffect(() => {
    const origin = "https://sarthiassociates.com";
    const url = `${origin}${path || (typeof window !== "undefined" ? window.location.pathname + window.location.search : "")}`;

    if (title) document.title = title;
    if (lang) document.documentElement.setAttribute("lang", lang);

    const upsertMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image || `${origin}/sarthi-logo.png`);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image || `${origin}/sarthi-logo.png`);

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    // Optional JSON-LD (single per-page schema block, id used to identify + replace)
    const scriptId = "sarthi-page-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = scriptId;
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }
  }, [title, description, path, image, JSON.stringify(jsonLd || {}), lang]);
}
